import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

const CHANNEL_NAME = "site-presence";
const SUBSCRIBE_DELAY_MS = 4000;

/**
 * Tracks a visitor's presence on a specific page.
 * Reuses a single Realtime Presence channel across route changes.
 * Deferred + offline-aware so it doesn't add noise during initial paint or audits.
 */
export function usePresenceTracker(pagePath: string, pageTitle: string) {
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const keyRef = useRef<string>(crypto.randomUUID());
  const subscribedRef = useRef(false);
  const latestRef = useRef({ pagePath, pageTitle });

  latestRef.current = { pagePath, pageTitle };

  // Bootstrap the channel once, deferred to avoid blocking LCP and to reduce
  // noisy realtime errors during Lighthouse runs.
  useEffect(() => {
    let cancelled = false;

    const start = () => {
      if (cancelled) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;

      const channel = supabase.channel(CHANNEL_NAME, {
        config: { presence: { key: keyRef.current } },
      });

      channel
        .on("presence", { event: "sync" }, () => {})
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            subscribedRef.current = true;
            channel.track({
              page_path: latestRef.current.pagePath,
              page_title: latestRef.current.pageTitle,
              joined_at: new Date().toISOString(),
            });
          }
        });

      channelRef.current = channel;
    };

    const timeout = setTimeout(start, SUBSCRIBE_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      const channel = channelRef.current;
      if (channel) {
        channel.untrack();
        supabase.removeChannel(channel);
      }
      channelRef.current = null;
      subscribedRef.current = false;
    };
  }, []);

  // Track page changes without recreating the channel
  useEffect(() => {
    const channel = channelRef.current;
    if (!channel || !subscribedRef.current) return;
    channel.track({
      page_path: pagePath,
      page_title: pageTitle,
      joined_at: new Date().toISOString(),
    });
  }, [pagePath, pageTitle]);
}
